import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatDateTimeShort } from "@/lib/bookingDates";
import { isZvtConfigured, type DeviceServiceSettings } from "@/lib/deviceService";
import { formatCurrency, parseAmount } from "@/lib/money";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import {
  availableRefundChannels,
  initialRefundChoice,
  paymentMethodForChannel,
  payoutMethodLabel,
  preferredOriginalPayment,
  recommendedPayoutAmount,
  refundChannelLabel,
  refundChannelOf,
  type RefundChannel,
} from "@/lib/refundChannel";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type { Invoice, Payment, PayoutCreatePayload } from "@/types/camping";

function todayLocalDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function localDateToIso(dateStr: string): string {
  return `${dateStr}T12:00:00.000Z`;
}

function openAmountNumber(invoice: Invoice): number {
  const raw = invoice.open_amount ?? invoice.total_amount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function amountsMatch(left: number, right: number): boolean {
  return Math.abs(left - right) < 0.001;
}

export interface InvoiceCreditPayoutSectionProps {
  invoice: Invoice;
  canRecord: boolean;
  onPayoutRecorded?: () => void;
  /** Summe „ausgezahlt / offen“ ausblenden, wenn sie woanders schon steht. */
  showSummary?: boolean;
}

export function InvoiceCreditPayoutSection({
  invoice,
  canRecord,
  onPayoutRecorded,
  showSummary = true,
}: InvoiceCreditPayoutSectionProps) {
  const [payouts, setPayouts] = useState<Payment[]>([]);
  const [referencePayments, setReferencePayments] = useState<Payment[]>([]);
  const [referencesSettled, setReferencesSettled] = useState(!invoice.reference_invoice_id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [amount, setAmount] = useState("");
  const [channel, setChannel] = useState<RefundChannel>("cash");
  const [paidDate, setPaidDate] = useState(todayLocalDate());
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [deviceService, setDeviceService] = useState<DeviceServiceSettings | null>(null);
  const [stripeConfigured, setStripeConfigured] = useState<boolean | null>(null);
  const [referencePaymentId, setReferencePaymentId] = useState("");
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);

  const showSection = isInvoiceFinalized(invoice.status);
  const zvtConfigured = isZvtConfigured(deviceService);
  const open = openAmountNumber(invoice);
  const canPayout = canRecord && open > 0 && invoice.status !== "cancelled";
  const preferred = preferredOriginalPayment(referencePayments);
  const preferredChannel = preferred ? refundChannelOf(preferred) : null;
  const channels = availableRefundChannels(referencePayments);
  const needsOriginalPayment = channel === "stripe" || channel === "terminal";
  const channelPayments = referencePayments.filter((payment) => refundChannelOf(payment) === channel);
  const selectedOriginal = channelPayments.find((payment) => payment.id === referencePaymentId) ?? null;
  const selectedOriginalAmount = selectedOriginal ? Number(selectedOriginal.amount) : null;
  const automaticRefund = needsOriginalPayment;

  const loadPayouts = useCallback(() => {
    if (!showSection) return;
    setLoading(true);
    setError(null);
    api
      .get<Payment[]>(`/api/v1/invoices/${invoice.id}/payments`)
      .then(setPayouts)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [invoice.id, showSection]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  useEffect(() => {
    if (!invoice.reference_invoice_id) {
      setReferencePayments([]);
      setReferencesSettled(true);
      return;
    }
    setReferencesSettled(false);
    api
      .get<Payment[]>(`/api/v1/invoices/${invoice.reference_invoice_id}/payments`)
      .then(setReferencePayments)
      .catch(() => setReferencePayments([]))
      .finally(() => setReferencesSettled(true));
  }, [invoice.reference_invoice_id]);

  useEffect(() => {
    api
      .get<{ device_service?: DeviceServiceSettings; stripe?: { configured?: boolean } }>(
        "/api/v1/settings",
      )
      .then((settings) => {
        setDeviceService(settings.device_service ?? null);
        setStripeConfigured(settings.stripe?.configured ?? false);
      })
      .catch(() => {
        setDeviceService(null);
        setStripeConfigured(null);
      });
  }, []);

  function applyChannel(next: RefundChannel, payments: Payment[] = referencePayments) {
    const pool = payments.filter((payment) => refundChannelOf(payment) === next);
    const pick = preferredOriginalPayment(pool);
    setChannel(next);
    setReferencePaymentId(pick?.id ?? "");
    setAmount(recommendedPayoutAmount(next, pick ? Number(pick.amount) : null, open));
  }

  function openRecordModal() {
    const choice = initialRefundChoice(referencePayments, open, {
      zvtReady: zvtConfigured,
      stripeReady: stripeConfigured !== false,
    });
    setChannel(choice.channel);
    setReferencePaymentId(choice.paymentId);
    setAmount(choice.amount);
    setFallbackNote(choice.fallbackNote);
    setPaidDate(todayLocalDate());
    setReference("");
    setNotes("");
    setError(null);
    setModalOpen(true);
  }

  async function submitPayout() {
    const parsed = parseAmount(amount);
    if (parsed == null || parsed <= 0) {
      setError("Bitte einen gültigen Betrag größer 0 eingeben.");
      return;
    }
    if (parsed > open + 0.0001) {
      setError(
        `Der Betrag darf den offenen Restbetrag (${formatCurrency(String(open), invoice.currency)}) nicht übersteigen.`,
      );
      return;
    }
    if (needsOriginalPayment && !referencePaymentId) {
      setError("Bitte die ursprüngliche Zahlung für die Erstattung auswählen.");
      return;
    }
    if (channel === "terminal" && !zvtConfigured) {
      setError("EC-Terminal ist nicht konfiguriert (Stammdaten → Schnittstellen → EC-Kartenterminal).");
      return;
    }
    if (channel === "stripe" && stripeConfigured === false) {
      setError("Stripe ist nicht konfiguriert (Stammdaten → Schnittstellen → Stripe).");
      return;
    }
    if (
      channel === "terminal" &&
      selectedOriginalAmount != null &&
      !amountsMatch(parsed, selectedOriginalAmount)
    ) {
      setError(
        `Das EC-Terminal storniert nur den vollen Kartenbetrag (${formatCurrency(String(selectedOriginalAmount), invoice.currency)}).`,
      );
      return;
    }
    if (
      channel === "stripe" &&
      selectedOriginalAmount != null &&
      parsed > selectedOriginalAmount + 0.0001
    ) {
      setError(
        `Stripe kann höchstens ${formatCurrency(String(selectedOriginalAmount), invoice.currency)} dieser Zahlung erstatten.`,
      );
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload: PayoutCreatePayload = {
        invoice_id: invoice.id,
        amount: parsed.toFixed(2),
        payment_method: paymentMethodForChannel(channel),
        paid_at: localDateToIso(paidDate),
        use_terminal: channel === "terminal",
        use_stripe: channel === "stripe",
      };
      const ref = reference.trim();
      const note = notes.trim();
      if (!automaticRefund && ref) payload.reference = ref;
      if (!automaticRefund && note) payload.notes = note;
      if (automaticRefund && note) payload.notes = note;
      if (automaticRefund && referencePaymentId) payload.reference_payment_id = referencePaymentId;

      const recorded = await api.post<Payment>("/api/v1/payments/payout", payload);
      setModalOpen(false);
      setNotice(recorded.cash_drawer_message ?? null);
      loadPayouts();
      onPayoutRecorded?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Auszahlung konnte nicht verbucht werden.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!showSection) return null;

  const terminalMismatch =
    channel === "terminal" &&
    selectedOriginalAmount != null &&
    parseAmount(amount) != null &&
    !amountsMatch(parseAmount(amount) ?? 0, selectedOriginalAmount);
  const remainder = (() => {
    const parsed = parseAmount(amount);
    if (parsed == null || parsed + 0.001 >= open) return null;
    return open - parsed;
  })();

  return (
    <div className="mt-3 border-t border-separator pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-label">Auszahlungen</h4>
        {canPayout ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!referencesSettled}
            onClick={openRecordModal}
          >
            Auszahlung erfassen
          </Button>
        ) : null}
      </div>

      {showSummary && (invoice.paid_amount != null || invoice.open_amount != null) ? (
        <p className="mt-1 text-xs text-label-secondary tabular-nums">
          Ausgezahlt: {formatCurrency(invoice.paid_amount ?? "0", invoice.currency)}
          {" · "}
          Offen: {formatCurrency(invoice.open_amount ?? "0", invoice.currency)}
        </p>
      ) : null}

      {error && !modalOpen ? <p className="mt-2 text-xs text-status-red">{error}</p> : null}
      {notice ? <p className="mt-2 text-xs text-label-secondary">{notice}</p> : null}
      {loading ? (
        <p className="mt-2 text-xs text-label-tertiary">Lade Auszahlungen…</p>
      ) : payouts.length === 0 ? (
        <p className="mt-2 text-xs text-label-tertiary">Noch keine Auszahlungen verbucht.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {payouts.map((payout) => (
            <li
              key={payout.id}
              className="flex items-center justify-between gap-3 rounded-lg bg-fill-tertiary/70 px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block text-sm text-label">{payoutMethodLabel(payout)}</span>
                <span className="mt-0.5 block text-xs text-label-secondary">
                  {formatDateTimeShort(payout.paid_at)}
                  {payout.reference ? ` · ${payout.reference}` : ""}
                </span>
              </span>
              <span className="shrink-0 tabular-nums text-sm font-semibold text-label">
                −{formatCurrency(payout.amount, invoice.currency)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        title="Auszahlung erfassen"
        description={`Gutschrift ${invoice.invoice_number} · offen ${formatCurrency(String(open), invoice.currency)}`}
        size="md"
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submitPayout();
          }}
        >
          <p className="text-sm text-label-secondary">
            Die Erstattung geht bevorzugt über das ursprüngliche Zahlungsmittel zurück. Die Art kann
            geändert werden.
          </p>

          {preferred ? (
            <p className="rounded-md border border-separator bg-fill-tertiary px-3 py-2 text-sm text-label-secondary">
              Ursprüngliche Zahlung: {refundChannelLabel(refundChannelOf(preferred))},{" "}
              {formatCurrency(preferred.amount, invoice.currency)} am {formatDateTimeShort(preferred.paid_at)}.
              {fallbackNote ? ` ${fallbackNote}` : ""}
              {preferredChannel && channel !== preferredChannel && !fallbackNote
                ? " Die gewählte Art weicht davon ab."
                : ""}
            </p>
          ) : (
            <p className="text-xs text-label-secondary">
              {invoice.reference_invoice_id
                ? "Auf der Referenzrechnung sind keine Zahlungen hinterlegt. Die Auszahlung wird manuell verbucht."
                : "Keine Referenzrechnung. Die Auszahlung wird manuell verbucht."}
            </p>
          )}

          <label className="flex flex-col gap-1 text-sm">
            Betrag ({invoice.currency})
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={open}
              required
              className="eh-input tabular-nums"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            Auszahlungsart
            <select
              className="eh-input"
              value={channel}
              onChange={(e) => applyChannel(e.target.value as RefundChannel)}
            >
              {channels.map((option) => (
                <option key={option} value={option}>
                  {refundChannelLabel(option)}
                  {preferredChannel === option ? " (ursprünglich)" : ""}
                </option>
              ))}
            </select>
          </label>

          {channel === "stripe" ? (
            <p className="rounded-md border border-separator bg-fill-tertiary px-3 py-2 text-sm text-label-secondary">
              Der Betrag wird über Stripe auf das ursprüngliche Zahlungsmittel erstattet.
            </p>
          ) : null}

          {channel === "terminal" ? (
            <p className="rounded-md border border-separator bg-fill-tertiary px-3 py-2 text-sm text-label-secondary">
              Das Storno wird am EC-Terminal ausgelöst. Bitte Karte am Terminal vorhalten. Storniert
              wird der volle Betrag der ursprünglichen Kartenzahlung.
            </p>
          ) : null}

          {needsOriginalPayment ? (
            channelPayments.length > 0 ? (
              <label className="flex flex-col gap-1 text-sm">
                Ursprüngliche Zahlung
                <select
                  className="eh-input"
                  required
                  value={referencePaymentId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    setReferencePaymentId(nextId);
                    const next = channelPayments.find((payment) => payment.id === nextId);
                    setAmount(recommendedPayoutAmount(channel, next ? Number(next.amount) : null, open));
                  }}
                >
                  {channelPayments.map((payment) => (
                    <option key={payment.id} value={payment.id}>
                      {formatDateTimeShort(payment.paid_at)} ·{" "}
                      {formatCurrency(payment.amount, invoice.currency)}
                      {payment.reference ? ` · Ref. ${payment.reference}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p className="text-xs text-label-secondary">
                Keine passende ursprüngliche Zahlung für diese Auszahlungsart.
              </p>
            )
          ) : null}

          {terminalMismatch && selectedOriginalAmount != null ? (
            <p className="text-xs text-status-red">
              Terminal-Storno nur über {formatCurrency(String(selectedOriginalAmount), invoice.currency)}. Für
              einen anderen Betrag Bar oder Stripe wählen.
            </p>
          ) : null}

          {channel === "terminal" && !zvtConfigured ? (
            <p className="text-xs text-label-secondary">
              EC-Terminal ist nicht konfiguriert. Die Auszahlung kann bar oder manuell verbucht werden.
            </p>
          ) : null}

          {remainder != null ? (
            <p className="text-xs text-label-secondary tabular-nums">
              Danach bleiben {formatCurrency(remainder, invoice.currency)} offen.
            </p>
          ) : null}

          <label className="flex flex-col gap-1 text-sm">
            Auszahlungsdatum
            <input
              type="date"
              required
              className="eh-input"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
            />
          </label>

          {!automaticRefund ? (
            <label className="flex flex-col gap-1 text-sm">
              Referenz <span className="text-label-tertiary">(optional)</span>
              <input
                type="text"
                className="eh-input"
                maxLength={128}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="z. B. Transaktions-ID"
              />
            </label>
          ) : null}

          <label className="flex flex-col gap-1 text-sm">
            Notiz <span className="text-label-tertiary">(optional)</span>
            <textarea
              className="eh-input min-h-[2.5rem] resize-y"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          {error ? <p className="text-sm text-status-red">{error}</p> : null}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              disabled={submitting}
              onClick={() => setModalOpen(false)}
            >
              Abbrechen
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting || terminalMismatch}>
              {channel === "stripe"
                ? "Über Stripe erstatten"
                : channel === "terminal"
                  ? "Am EC-Terminal auszahlen"
                  : channel === "cash"
                    ? "Bar auszahlen"
                    : "Auszahlung verbuchen"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
